import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  const handleLogout = () => {
    logout();
    if (user?.role === 'contributor') {
      navigate('/contributor/login');
    } else {
      navigate('/admin/login');
    }
  };

  // Menu adapté selon le rôle.
  const menuItems = user?.role === 'contributor' 
    ? [
        { path: '/admin', icon: 'tachometer-alt', label: 'Dashboard' },
        { path: '/admin/articles/new', icon: 'pen', label: 'Nouvel article' },
        { path: '/admin/submissions', icon: 'tasks', label: 'Mes soumissions' },
        { path: '/admin/articles', icon: 'newspaper', label: 'Mes articles' },
        { path: '/admin/profile', icon: 'user-circle', label: 'Mon profil' },
      ]
    : [
        { path: '/admin', icon: 'tachometer-alt', label: 'Dashboard', adminOnly: false },
        { path: '/admin/articles', icon: 'newspaper', label: 'Articles', adminOnly: false },
        { path: '/admin/submissions', icon: 'tasks', label: 'Soumissions', adminOnly: false },
        { path: '/admin/categories', icon: 'folder', label: 'Catégories', adminOnly: true },
        { path: '/admin/users', icon: 'users', label: 'Utilisateurs', adminOnly: true },
        { path: '/admin/profile-tags', icon: 'tags', label: 'Tags de profil', adminOnly: true },
        { path: '/admin/user-tags', icon: 'user-tag', label: 'Assigner tags', adminOnly: true },
        { path: '/admin/rewards', icon: 'coins', label: 'Récompenses', adminOnly: true },
        { path: '/admin/theme', icon: 'palette', label: 'Thème', adminOnly: true },
        { path: '/admin/profile', icon: 'user-circle', label: 'Mon profil', adminOnly: false },
      ];

  const filteredMenu = menuItems.filter(item => !item.adminOnly || user?.role === 'admin');

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <div className="w-64 bg-surface border-r border-gray-700/30 flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-gray-700/30">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center">
              <i className="fas fa-sun text-gray-900 text-xl"></i>
            </div>
            <div>
              <span className="text-xl font-bold text-white">
                Sun<span className="text-yellow-400">Serv</span>
              </span>
              <p className="text-xs text-gray-400">Admin Blog</p>
            </div>
          </div>
        </div>

        {/* Menu */}
        <nav className="flex-1 p-4 space-y-2">
          {filteredMenu.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                location.pathname === item.path
                  ? 'bg-yellow-400 text-gray-900 font-semibold'
                  : 'text-gray-300 hover:bg-surface/80'
              }`}
            >
              <i className={`fas fa-${item.icon} w-5`}></i>
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* User info & Logout */}
        <div className="p-4 border-t border-gray-700/30">
          <div className="flex items-center space-x-3 mb-3">
            {user?.avatar ? (
              <img 
                src={user.avatar} 
                alt={user.display_name || user.username}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center">
                <i className="fas fa-user text-gray-900"></i>
              </div>
            )}
            <div className="flex-1">
              <p className="text-sm font-medium text-white">{user?.display_name || user?.username}</p>
              <p className="text-xs text-gray-400">
                {user?.role === 'admin' ? 'Administrateur' : user?.role === 'redacteur' ? 'Rédacteur' : 'Contributeur'}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full bg-red-500/10 hover:bg-red-500/20 text-red-400 py-2 rounded-lg transition-colors text-sm font-medium"
          >
            <i className="fas fa-sign-out-alt mr-2"></i>
            Déconnexion
          </button>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="block mt-2 w-full bg-gray-700/30 hover:bg-gray-700/50 text-gray-300 py-2 rounded-lg transition-colors text-sm font-medium text-center"
          >
            <i className="fas fa-external-link-alt mr-2"></i>
            Voir le blog
          </a>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {children}
      </div>
    </div>
  );
};

export default AdminLayout;
